import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";

export default function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Helmet>
        <title>Terms of Service | Fixus</title>
        <meta name="description" content="Fixus Terms of Service — the platform is a listing service. Users transact directly with each other." />
        <meta name="robots" content="noindex" />
      </Helmet>
      <h1 className="font-display font-bold text-3xl text-slate-900 mb-2" data-testid="terms-heading">Terms of Service</h1>
      <p className="text-sm text-slate-500 mb-8">Last updated: {new Date().toISOString().slice(0, 10)}</p>

      <Card className="border-slate-200">
        <CardContent className="p-6 sm:p-8 prose prose-slate max-w-none text-slate-700 leading-relaxed">
          <p className="font-semibold text-slate-900">
            Please read these Terms carefully. By creating a Fixus account you confirm you have read, understood, and agree to them.
          </p>

          <h2 className="font-display text-xl mt-8 mb-2 text-slate-900">1. What Fixus is (and is not)</h2>
          <p>
            Fixus (operated by FriendSoft Corp) is an online listing and discovery platform that helps homeowners and independent tradespeople (&quot;Contractors&quot;) find each other in Canada. Fixus <strong>is not</strong> a party to any agreement, work performed, or payment between homeowners and Contractors. Fixus does not employ, endorse, insure, license, or supervise any Contractor listed on the platform.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">2. Direct relationship between users</h2>
          <p>
            Any job posted, quote sent, price agreed, service delivered, warranty offered, or dispute arising is <strong>strictly between the homeowner and the Contractor</strong>. Users are solely responsible for:
          </p>
          <ul className="list-disc pl-6 my-2 space-y-1">
            <li>Verifying the other party&apos;s identity, licensing (e.g., Red Seal, ESA, TSSA), insurance, and WCB/WorkSafe coverage before hiring or working.</li>
            <li>Negotiating and agreeing on price, scope, timelines, materials, and payment terms in writing.</li>
            <li>Compliance with all applicable municipal, provincial, and federal laws (including building codes, permits, and tax obligations).</li>
            <li>Resolving any dispute, complaint, damage claim, or refund request between themselves.</li>
          </ul>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">3. No warranty; no liability</h2>
          <p>
            Fixus provides the platform &quot;as is&quot; without warranty of any kind. To the maximum extent permitted by law, Fixus and FriendSoft Corp <strong>disclaim all liability</strong> for any direct, indirect, incidental, consequential, or special damages arising out of or related to: (a) any Contractor&apos;s work quality, safety, timeliness, licensing, or conduct; (b) any homeowner&apos;s payment, communication, or conduct; (c) any decision made based on information displayed on Fixus (ratings, reviews, certifications, or contact details); (d) any loss of data, revenue, profits, or goodwill.
          </p>
          <p>
            Where liability cannot be excluded by law, Fixus&apos;s aggregate liability to any user for any claim is limited to <strong>CAD $50.00</strong>.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">4. Ratings, reviews & listing content</h2>
          <p>
            Reviews and ratings are user-generated content. Fixus does not independently verify their accuracy and is not responsible for them. Contractors are responsible for the accuracy of their own listing content, including business name, business number, certifications, and service areas. False or misleading claims may result in listing removal without notice.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">5. User conduct</h2>
          <p>
            You agree not to use Fixus for any unlawful purpose, to harass other users, to post false or defamatory content, to circumvent the platform&apos;s features, or to scrape data from the platform. Fixus may suspend or remove any account at its discretion.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">6. Privacy</h2>
          <p>
            Fixus collects and processes personal data as described in our Privacy Notice (available on request). You consent to Fixus contacting you at the email address you provide for account and transactional purposes. Fixus complies with the Personal Information Protection and Electronic Documents Act (PIPEDA) and Canada&apos;s Anti-Spam Legislation (CASL).
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">7. Payments</h2>
          <p>
            Fixus does not process payments between homeowners and Contractors. All monies are exchanged directly between the parties. Fixus is not responsible for lost, delayed, disputed, or fraudulent payments.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">8. Governing law &amp; disputes with Fixus</h2>
          <p>
            These Terms are governed by the laws of the Province of Alberta and the federal laws of Canada applicable in Alberta. Any dispute between you and Fixus (not a dispute between users) will be resolved exclusively in the courts of Calgary, Alberta.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">9. Changes to these Terms</h2>
          <p>
            Fixus may update these Terms at any time. Material changes will be signalled by an updated &quot;Last updated&quot; date at the top of this page. Continued use of the platform after an update constitutes acceptance of the new Terms.
          </p>

          <h2 className="font-display text-xl mt-6 mb-2 text-slate-900">10. Contact</h2>
          <p>
            For questions about these Terms, contact FriendSoft Corp via the email address you used to sign up (reply to any Fixus transactional email).
          </p>

          <p className="mt-8 text-xs text-slate-500 italic">
            This document is a plain-language contract summary. It is not legal advice. Fixus recommends both homeowners and Contractors consult their own legal, tax, and insurance advisors before entering into any home-services agreement.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
